/**
 * Bannere: the list, and the way into the studio.  CA-40; banner studio 2026-10-31.
 *
 * Making and changing a banner is the studio's (views/BannerStudio.tsx), a page of its own under this address:
 * #/bannere/ny, #/bannere/<id> (a draft), #/bannere/ny?fra=<id> (a copy), #/bannere/ny?arrangement=<id> (for a
 * program). This file keeps the list: every banner drawn small as the screens draw it, what it is now, and exactly
 * the buttons the server says THIS person may press (`actions`).
 *
 * The lifecycle (banner hardening, 2026-10-03; docs/platform-implementation/BANNER_LIFECYCLE.md): everything made in
 * the studio is a DRAFT. It is published here — by the person who wrote it, or by anybody with «Godkjenne og
 * publisere bannere» (owner 2026-10-23) — and only then is it on the screens (and in the member app, when «Vis også i
 * appen» is ticked). Stopping and taking down need nobody else, and taking down for good is two clicks.
 *
 * Besides «Endre», «Publiser», «Stopp», «Til utkast», «Ta ned», «Del» and «Skriv ut»: «Lag en kopi» (last year's Eid
 * banner again), «Last ned» (the banner as a 1920×1080 picture) and «Bruk på arrangement» (the banner as a program's
 * picture, drawn here and sent through the program's own picture route — lib/banner-attach).
 */
import React, { useEffect, useState } from 'react';
import { blobUrl, can as canDo, errText, post, saveBlob, type Body } from '../lib/api';
import { useLoad, useMsg } from '../lib/hooks';
import { useNav } from '../lib/nav';
import { osloTime, wallClock } from '../lib/format';
import { Btn, Card, Field, LoadError, Loading, Msg, Tag, TwoStep, type TagTone } from '../components/ui';
import { TargetPicker } from '../components/TargetPicker';
import { Social } from '../core';
import { SharePanel } from '../components/SharePanel';
import { shows as sharesSocial, drawScreenImage, mosquePublic } from '../lib/social';
import { PosterPanel } from '../components/PosterPanel';
import { BannerThumb } from '../components/BannerFace';
import { attachToEvent, bannerTitle, sourceOf } from '../lib/banner-attach';
import { BannerStudio, FLASH_KEY } from './BannerStudio';
import { SeenCount, useSeenCounts } from '../components/SeenCount';

/** What a banner IS now (core banners.stateOf), in words, with its tag colour. */
const STATE: Record<string, [string, TagTone]> = {
  draft: ['utkast — ikke publisert', 'grey'], scheduled: ['publisert, vises senere', 'gold'], live: ['vises nå', 'green'],
  expired: ['utløpt', 'grey'], cancelled: ['stoppet', 'grey'], archived: ['tatt ned', 'grey'],
};
/** A stored time as it is read: an instant in Oslo time, or the wall-clock time it was typed as. */
const when = (v: string | null | undefined) => (!v ? '' : /(Z|[+-]\d\d:\d\d)$/.test(v) ? osloTime(v) : wallClock(v));

/** The decoded hash path and query ('/bannere/ny?fra=…'); a malformed address is the list. */
function hashRoute(): { route: string; query: URLSearchParams } {
  try {
    const h = location.hash.replace(/^#/, '');
    const [p, q] = h.split('?');
    return { route: decodeURIComponent(p || '/bannere'), query: new URLSearchParams(q || '') };
  } catch { return { route: '/bannere', query: new URLSearchParams() }; }
}

/** The picture itself, for a picture banner's «Last ned» (the staff route; a draft's picture is not public). */
async function pictureBlob(id: string): Promise<Blob | null> {
  const url = await blobUrl(`/admin/banners/${encodeURIComponent(id)}/image`);
  if (!url) return null;
  try { return await (await fetch(url)).blob(); } catch { return null; } finally { URL.revokeObjectURL(url); }
}

/** «Bruk på arrangement»: pick one of the programs; the banner is drawn and becomes its picture. */
function UseOnEvent({ banner, onClose, onDone }: { banner: Body; onClose: () => void; onDone: (text: string, ok: boolean) => void }) {
  const { data, error, reload } = useLoad<Body>('/events');
  const [eventId, setEventId] = useState('');
  const [busy, setBusy] = useState(false);
  const items: Body[] = ((data && data.items) || []).filter((e: Body) => !['cancelled', 'archived'].includes(e.status));
  return (
    <div className="share" role="region" aria-label={`Bruk «${bannerTitle(banner)}» på et arrangement`}>
      {error ? <LoadError error={error} retry={reload} /> : !data ? <Loading /> : (
        <div className="row" style={{ alignItems: 'flex-end' }}>
          <Field label="Arrangement" className="field half" note="Banneret tegnes i skjermstørrelse og blir arrangementets bilde i appen, på skjermene og på nettsiden.">{(id, d) => (
            <select id={id} aria-describedby={d} value={eventId} onChange={(e) => setEventId(e.target.value)}>
              <option value="">Velg …</option>
              {items.map((e) => <option key={e.id} value={e.id}>{e.title_no} · {wallClock(e.starts_at)}</option>)}
            </select>)}</Field>
          <Btn disabled={!eventId || busy} onClick={async () => {
            setBusy(true);
            try {
              const bad = await attachToEvent(eventId, banner);
              const ev = items.find((e) => e.id === eventId);
              onDone(bad ? `Bildet ble ikke lagt på arrangementet: ${bad}` : `«${bannerTitle(banner)}» er lagt på «${ev ? ev.title_no : 'arrangementet'}» som bilde.`, !bad);
            } finally { setBusy(false); }
          }}>{busy ? 'Legger på …' : 'Legg på arrangementet'}</Btn>
          <Btn ghost onClick={onClose}>Lukk</Btn>
          {!items.length && <p className="note">Ingen kommende arrangementer. Lag ett under «Arrangementer» først.</p>}
        </div>
      )}
    </div>
  );
}

export function Banners() {
  const [{ route, query }, setRoute] = useState(hashRoute);
  useEffect(() => {
    const on = () => setRoute(hashRoute());
    window.addEventListener('hashchange', on);
    return () => window.removeEventListener('hashchange', on);
  }, []);
  const sub = route.replace(/^\/bannere\/?/, '');
  if (sub) return <BannerStudio key={route + '?' + query.toString()} id={sub === 'ny' ? null : sub} query={query} />;
  return <BannerList />;
}

function BannerList() {
  const { data, error, reload } = useLoad<Body>('/banners');
  const [listMsg, sayList] = useMsg();
  const [acting, setActing] = useState<string | null>(null);
  const { refreshBadges, caps } = useNav();
  // «Del i sosiale medier» (2026-10-22): the row whose share panel is open, if any.
  const [sharing, setSharing] = useState<string | null>(null);
  // «Skriv ut» (MKK #34, 2026-10-29): the row whose poster panel is open, if any — any banner in the list, A4 or A3.
  const [printing, setPrinting] = useState<string | null>(null);
  // «Bruk på arrangement» (2026-10-31): the row whose program picker is open, if any.
  const [using, setUsing] = useState<string | null>(null);
  const canShare = sharesSocial(caps, canDo);
  const mayUseOnEvent = canDo('events.write');
  // «Vist for M (siste 30 dager)» (spec 01 C): signed-in app users who had it on screen — only with «Visningstall» on.
  const seen = useSeenCounts('banner', ((data && data.items) || []).map((b: Body) => b.id), 30);
  // What the studio did before coming back here (one message, read once).
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(FLASH_KEY);
      if (raw) { sessionStorage.removeItem(FLASH_KEY); const f = JSON.parse(raw); sayList(String(f.text || ''), f.tone === 'err' ? 'err' : 'ok'); }
    } catch { /* nothing to say */ }
  }, [sayList]);

  if (error) return <LoadError error={error} retry={reload} />;
  if (!data) return <Loading />;

  const items: Body[] = data.items || [];
  async function downloadBanner(b: Body) {
    sayList('');
    const title = bannerTitle(b);
    const blob = b.template === 'image' ? await pictureBlob(b.id) : await drawScreenImage(sourceOf(b), (await mosquePublic()).shortName, 'image/png');
    if (!blob) return sayList('Bildet kunne ikke lages i denne nettleseren.', 'err');
    const ext = b.template === 'image' ? (blob.type === 'image/png' ? 'png' : blob.type === 'image/webp' ? 'webp' : 'jpg') : 'png';
    saveBlob(blob, Social.fileName(title, 'square', 'banner').replace(/-\d+x\d+\.jpg$/, b.template === 'image' ? `.${ext}` : '-1920x1080.png'));
  }
  return (
    <>
      <div className="new-row"><a className="btn" href="#/bannere/ny">+ Nytt banner</a></div>
      <Card title={'Bannere · ' + items.length} className="bnr-list">
        {items.length ? items.map((b) => {
          const title = bannerTitle(b);
          const fitBad = b.fit && Object.values(b.fit as Record<string, Body>).some((f) => f && f.fits === false);
          const can = (a: string) => Array.isArray(b.actions) && b.actions.includes(a);
          const [stateText, stateTone] = STATE[b.state] || [b.state, 'grey'];
          const act = async (path: string, body?: Body) => {
            if (acting) return;
            setActing(b.id);
            const r = await post(`/banners/${encodeURIComponent(b.id)}/${path}`, body || {}).finally(() => setActing(null));
            sayList(r.ok ? (r.body.message_no || 'Oppdatert.') : errText(r.body), r.ok ? 'ok' : 'err');
            reload(); refreshBadges();
          };
          const who = [b.createdByName && `laget av ${b.createdByName}`,
            b.updatedByName && `endret av ${b.updatedByName}`,
            b.approvedByName && `godkjent av ${b.approvedByName}${b.approvedAt ? ' ' + osloTime(b.approvedAt) : ''}`].filter(Boolean).join(' · ');
          const span = [b.startsAt && `fra ${when(b.startsAt)}`, b.endsAt && `til ${when(b.endsAt)}`].filter(Boolean).join(' ');
          const shareOpen = sharing === b.id && Social.shareable('banner', b);
          const kindWord = b.image && b.template === 'image' ? `Bilde ${b.image.width}×${b.image.height}` : b.template === 'photo' ? 'Bilde med tekst' : (b.templateName || b.template);
          return (
            <React.Fragment key={b.id}>
            <div className="bn">
              <BannerThumb b={b} />
              <div><div className="nm">{title}</div>
                <div className="sub">{kindWord} · {stateText}{span ? ' · ' + span : ''}</div>
                {who && <div className="sub">{who}</div>}
                <SeenCount counts={seen} id={b.id} label="Vist for" suffix="(siste 30 dager)" block />
                <TargetPicker kind="banner" id={b.id} /></div>
              <div className="sp" style={{ flex: 1 }} />
              {fitBad && <Tag tone="gold">tekst blir liten</Tag>}
              {b.inApp && <Tag tone="green" title="Vises også i medlemsappen når det er publisert">i appen</Tag>}
              <Tag tone={stateTone}>{stateText}</Tag>
              {can('edit') && <a className="btn sm ghost" aria-label={`Endre «${title}»`} href={`#/bannere/${encodeURIComponent(b.id)}`}>Endre</a>}
              {/* The approval names the version on screen: a change saved since is refused (409), and the list reloads to show it. */}
              {/* UX audit U-05 (2026-10-19): «Publiser» and «Stopp» are two clicks, as «Ta ned» is. */}
              {can('approve') && <TwoStep ghost={false} go disabled={acting === b.id} ariaLabel={`Publiser «${title}»`} label="Publiser" busyLabel="Publiserer …"
                confirmLabel={b.inApp ? 'Klikk igjen: vises på skjermene og i appen' : 'Klikk igjen: vises på skjermene'} onConfirm={() => act('approve', { version: b.version })} />}
              {b.state === 'draft' && !can('approve') && <span className="sub">utkast — publiseres av den som laget det</span>}
              {can('cancel') && <TwoStep disabled={acting === b.id} ariaLabel={`Stopp «${title}»`} label="Stopp" busyLabel="Stopper …"
                confirmLabel={b.inApp ? 'Klikk igjen: tas av skjermene og ut av appen' : 'Klikk igjen: tas av skjermene'} onConfirm={() => act('status', { status: 'cancelled' })} />}
              {can('withdraw') && <Btn ghost aria-label={`Tilbake til utkast: «${title}»`} onClick={() => act('status', { status: 'draft' })}>Til utkast</Btn>}
              {/* Taken down for good is final (the API refuses to bring it back): two clicks, never a browser dialog. */}
              {can('archive') && <TwoStep label="Ta ned" confirmLabel={`Ta ned «${title}» for godt?`} onConfirm={() => act('status', { status: 'archived' })} />}
              {/* Only an approved banner, and only for somebody who writes announcements where the mosque has the capability. */}
              {canShare && Social.shareable('banner', b) && <Btn ghost aria-expanded={shareOpen} aria-label={`Del «${title}» i sosiale medier`}
                onClick={() => setSharing(shareOpen ? null : b.id)}>Del</Btn>}
              <Btn ghost aria-expanded={printing === b.id} aria-label={`Skriv ut «${title}» som plakat`}
                onClick={() => setPrinting(printing === b.id ? null : b.id)}>Skriv ut</Btn>
              {/* Banner studio (2026-10-31): the banner again as a new draft; as a picture; as a program's picture. */}
              <a className="btn sm ghost" aria-label={`Lag en kopi av «${title}»`} href={`#/bannere/ny?fra=${encodeURIComponent(b.id)}`}>Lag en kopi</a>
              <Btn ghost aria-label={`Last ned «${title}» som bilde`} onClick={() => void downloadBanner(b)}>Last ned</Btn>
              {mayUseOnEvent && b.state !== 'archived' && <Btn ghost aria-expanded={using === b.id} aria-label={`Bruk «${title}» på et arrangement`}
                onClick={() => setUsing(using === b.id ? null : b.id)}>Bruk på arrangement</Btn>}
            </div>
            {shareOpen && <SharePanel title={title} onClose={() => setSharing(null)} source={sourceOf(b)} />}
            {printing === b.id && <PosterPanel title={title} onClose={() => setPrinting(null)} source={sourceOf(b)} />}
            {using === b.id && <UseOnEvent banner={b} onClose={() => setUsing(null)} onDone={(text, ok) => { setUsing(null); sayList(text, ok ? 'ok' : 'err'); }} />}
            </React.Fragment>
          );
        }) : <div className="card-body">Ingen bannere ennå. Lag det første med «+ Nytt banner».</div>}
        <Msg text={listMsg.text} tone={listMsg.tone} style={{ margin: '0 18px 14px' }} />
      </Card>
    </>
  );
}
